/**
 * SignupGate — value-capture modal shown when a guest tries to perform an
 * action that requires an account (favorites, RSVPs, helpful votes, etc.).
 *
 * Usage:
 *   const requireAuth = useSignupGate();
 *   if (!(await requireAuth({ audience: "traveler", intent: "rsvp" }))) return;
 *   // ... proceed with the action
 *
 * Mount <SignupGateProvider> once near the app root (inside auth + router).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShip,
  faLockOpen,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/Modal";
import { trackEvent } from "../../utils/analytics";
import { useAuth } from "../../contexts/CombinedAuthContext";

const SignupGateContext = createContext(null);

const DEFAULT_COPY = {
  traveler: {
    title: "Hoist the colors, sailor",
    message:
      "Joining the Guild is free — no card, no commitment. Your free Deckhand pass unlocks deals, the Treasure Map, and Guild events across the islands.",
    primaryLabel: "Join Free as a Traveler",
  },
  business: {
    title: "Raise yer flag with the Guild",
    message:
      "List your business free with a Crew Listing. Get discovered by travelers exploring the Treasure Map and Guild deals — no listing fee, no contracts.",
    primaryLabel: "List My Business Free",
  },
  realestate: {
    title: "Drop anchor at the Guild",
    message:
      "List a short-term rental, long-term home, or roommate opening free to the Guild crew. Get pinned to the Treasure Map alongside trusted partners.",
    primaryLabel: "List My Property Free",
  },
};

function buildSignupUrl(audience, intent) {
  const params = new URLSearchParams();
  params.set("audience", audience || "traveler");
  if (intent) params.set("intent", intent);
  return `/signup?${params.toString()}`;
}

export function SignupGateProvider({ children }) {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const close = useCallback(
    (value) => {
      if (resolverRef.current) {
        resolverRef.current(value);
        resolverRef.current = null;
      }
      setOpen(false);
    },
    []
  );

  const requireAuth = useCallback(
    (options = {}) => {
      // Already signed in — bail out fast.
      if (isAuthenticated) return Promise.resolve(true);

      setOpts({
        audience: options.audience || "traveler",
        intent: options.intent || null,
        title: options.title || null,
        message: options.message || null,
        primaryLabel: options.primaryLabel || null,
      });
      setOpen(true);
      trackEvent("gate_shown", {
        refType: options.audience || "traveler",
        refId: options.intent || null,
      });
      return new Promise((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [isAuthenticated]
  );

  const handleJoinFree = () => {
    // Navigate to the SignupCTA page — it persists signup context to
    // sessionStorage and kicks off the Wix OAuth signup flow on click.
    const url = buildSignupUrl(opts.audience, opts.intent);
    trackEvent("gate_accepted", {
      refType: opts.audience || "traveler",
      refId: opts.intent || null,
      meta: { action: "join" },
    });
    close(true);
    navigate(url);
  };

  const handleSignIn = async () => {
    // Persist signup context anyway so post-login plumbing can use it.
    try {
      sessionStorage.setItem(
        "signupContext",
        JSON.stringify({
          audience: opts.audience || "traveler",
          tier: null,
          intent: opts.intent || null,
          ts: Date.now(),
        })
      );
    } catch (_e) {
      /* ignore */
    }
    trackEvent("gate_accepted", {
      refType: opts.audience || "traveler",
      refId: opts.intent || null,
      meta: { action: "signin" },
    });
    close(true);
    if (typeof login === "function") {
      try {
        await login();
      } catch (err) {
        console.error("[SignupGate] login error:", err);
      }
    }
  };

  const handleCancel = () => {
    trackEvent("gate_dismissed", {
      refType: opts.audience || "traveler",
      refId: opts.intent || null,
    });
    close(false);
  };

  const audienceCopy =
    DEFAULT_COPY[opts.audience] || DEFAULT_COPY.traveler;
  const title = opts.title || audienceCopy.title;
  const message = opts.message || audienceCopy.message;
  const primaryLabel = opts.primaryLabel || audienceCopy.primaryLabel;

  const value = useMemo(() => requireAuth, [requireAuth]);

  return (
    <SignupGateContext.Provider value={value}>
      {children}
      <Modal isOpen={open} onClose={handleCancel} size="small">
        <ModalHeader title={title} onClose={handleCancel} />
        <ModalBody>
          <p style={{ marginTop: 0 }}>{message}</p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "0.75rem 0 0",
              display: "grid",
              gap: "0.5rem",
              fontSize: "0.9rem",
            }}
          >
            <li>
              <FontAwesomeIcon icon={faLockOpen} style={{ marginRight: 8 }} />
              Free means free — no card, no surprise charges.
            </li>
            <li>
              <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: 8 }} />
              No spam, no sellin' yer secrets — ye have our word.
            </li>
            <li>
              <FontAwesomeIcon icon={faShip} style={{ marginRight: 8 }} />
              Walk the plank any time — export or delete from yer account.
            </li>
          </ul>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={handleCancel}
            className="marketing-cta"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={handleSignIn}
            className="marketing-cta"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={handleJoinFree}
            className="marketing-cta marketing-cta--bright"
          >
            {primaryLabel}
          </button>
        </ModalFooter>
      </Modal>
    </SignupGateContext.Provider>
  );
}

/**
 * Returns an imperative `requireAuth(opts)` function that resolves to true
 * when the visitor is (or becomes) authenticated, or false when they cancel.
 *
 * When the gate opens, choosing Join Free or Sign In redirects away — the
 * promise still resolves true so callers can clean up local state.
 */
export function useSignupGate() {
  const ctx = useContext(SignupGateContext);
  if (!ctx) {
    // Fallback: behave like an always-true gate so call sites don't crash
    // when the provider is absent (e.g., in unit tests).
    return async () => true;
  }
  return ctx;
}

export default SignupGateProvider;
